import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { SessionDocument, taskApi } from '@/services/api';
import { useJobPollingStore } from '@/stores/jobPollingStore';

interface MultiDocQAPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionDocuments: SessionDocument[];
}

function parseResourceId(resourceId: string): { id: string; type: string } | null {
  if (resourceId.startsWith('foreign_patent-'))
    return { type: 'foreign_patent', id: resourceId.slice('foreign_patent-'.length) };
  if (resourceId.startsWith('patent-'))
    return { type: 'patent', id: resourceId.slice('patent-'.length) };
  if (resourceId.startsWith('publication-'))
    return { type: 'publication', id: resourceId.slice('publication-'.length) };
  return null;
}

function classifyPatentNumber(raw: string): { cleanedNumber: string; docType: string; displayLabel: string } | null {
  if (!raw || !raw.trim()) return null;
  let input = raw.trim();

  // Foreign patent: starts with 2-3 uppercase letters that are NOT "US"
  const foreignMatch = input.match(/^([A-Z]{2,3})/i);
  if (foreignMatch && foreignMatch[1].toUpperCase() !== 'US') {
    const cleaned = input.replace(/,/g, '').replace(/\s/g, '').toUpperCase();
    const countryCode = foreignMatch[1].toUpperCase();
    return { cleanedNumber: cleaned, docType: 'foreign_patent', displayLabel: `Foreign Patent (${countryCode}) (${cleaned})` };
  }

  // Strip US prefix, kind code suffix, commas, slashes, spaces
  let cleaned = input
    .replace(/,/g, '')
    .replace(/\//g, '')
    .replace(/\s/g, '')
    .replace(/^US/i, '')
    .replace(/[A-Z]\d*$/i, '');

  if (!/^\d+$/.test(cleaned)) return null;

  // Publication: 10-11 digits starting with year 2xxx
  if ((cleaned.length === 10 || cleaned.length === 11) && /^2\d{3}/.test(cleaned)) {
    if (cleaned.length === 10) {
      cleaned = cleaned.slice(0, 4) + '0' + cleaned.slice(4);
    }
    return { cleanedNumber: cleaned, docType: 'publication', displayLabel: `US Publication (${cleaned})` };
  }

  const numVal = parseInt(cleaned, 10);

  // Application: 8 digits, value >= 14,000,000 — exclude from bulk ask (not supported)
  if (cleaned.length === 8 && numVal >= 14000000) {
    return null;
  }

  // Patent: 7-8 digits, value < 14,000,000
  if ((cleaned.length === 7 || cleaned.length === 8) && numVal < 14000000) {
    return { cleanedNumber: cleaned, docType: 'patent', displayLabel: `US Patent (${cleaned})` };
  }

  return null;
}

function formatResourceId(resourceId: string): string {
  if (!resourceId) return '';
  if (resourceId.startsWith('patent-')) return `US Patent ${resourceId.slice('patent-'.length)}`;
  if (resourceId.startsWith('publication-')) return `US Publication ${resourceId.slice('publication-'.length)}`;
  if (resourceId.startsWith('foreign_patent-')) return resourceId.slice('foreign_patent-'.length);
  return resourceId.replace('-', ' ');
}

const PATENT_TYPES = ['patent', 'publication', 'foreign_patent'];

export function MultiDocQAPanel({ open, onOpenChange, sessionDocuments }: MultiDocQAPanelProps) {
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [manualInput, setManualInput] = useState('');
  const [manualDocs, setManualDocs] = useState<{ id: string; type: string; label: string }[]>([]);
  const [manualInputError, setManualInputError] = useState('');
  const [question, setQuestion] = useState('');
  const [emailResults, setEmailResults] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [jobStatus, setJobStatus] = useState<'idle' | 'submitted' | 'done' | 'error'>('idle');
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { startPolling } = useJobPollingStore();

  const patentDocs = sessionDocuments.filter(doc => PATENT_TYPES.includes(doc.file_type || ''));

  // Listen for job-complete to update panel status
  useEffect(() => {
    if (!submittedJobId) return;
    const handler = (e: Event) => {
      const { jobId, success } = (e as CustomEvent).detail;
      if (jobId !== submittedJobId) return;
      setJobStatus(success ? 'done' : 'error');
      if (!success) setErrorMessage('Analysis failed. Please try again.');
    };
    window.addEventListener('job-complete', handler as EventListener);
    return () => window.removeEventListener('job-complete', handler as EventListener);
  }, [submittedJobId]);

  // Elapsed timer while submitted
  useEffect(() => {
    if (jobStatus === 'submitted') {
      setElapsedSeconds(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds(s => s + 1);
      }, 1000);
    } else {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
    return () => {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, [jobStatus]);

  // Reset form on close if idle
  const handleOpenChange = (val: boolean) => {
    if (!val && jobStatus === 'idle') {
      setSelectedDocIds(new Set());
      setManualInput('');
      setManualDocs([]);
      setManualInputError('');
      setQuestion('');
      setErrorMessage('');
    }
    onOpenChange(val);
  };

  const toggleDoc = (resourceId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  };

  const handleAddManual = () => {
    setManualInputError('');
    const entries = manualInput.split(/[\n;]+/).map(s => s.trim()).filter(Boolean);
    if (!entries.length) return;

    const failed: string[] = [];
    const toAdd: { id: string; type: string; label: string }[] = [];

    for (const entry of entries) {
      const classified = classifyPatentNumber(entry);
      if (!classified) {
        failed.push(entry);
        continue;
      }
      const alreadyExists =
        manualDocs.some(d => d.id === classified.cleanedNumber && d.type === classified.docType) ||
        toAdd.some(d => d.id === classified.cleanedNumber && d.type === classified.docType);
      if (!alreadyExists) {
        toAdd.push({ id: classified.cleanedNumber, type: classified.docType, label: classified.displayLabel });
      }
    }

    if (toAdd.length) setManualDocs(prev => [...prev, ...toAdd]);
    if (failed.length) {
      setManualInputError(`Could not recognize: ${failed.join(', ')}`);
    } else {
      setManualInput('');
    }
  };

  const handleRemoveManual = (index: number) => {
    setManualDocs(prev => prev.filter((_, i) => i !== index));
  };

  const totalCount = selectedDocIds.size + manualDocs.length;
  const canSubmit = totalCount > 0 && question.trim().length > 0 && !submitting && jobStatus !== 'submitted';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage('');

    try {
      // Build resources array
      const resources: { id: string; type: string }[] = [];

      for (const resourceId of selectedDocIds) {
        const parsed = parseResourceId(resourceId);
        if (parsed) resources.push(parsed);
      }

      for (const doc of manualDocs) {
        resources.push({ id: doc.id, type: doc.type });
      }

      const result = await taskApi.submitBulkAskTask(resources, question.trim(), emailResults) as any;
      const jobId = result?.job_id;

      if (!jobId) {
        throw new Error('No job ID returned from server');
      }

      // Build a short label for notifications
      const docLabel = resources.slice(0, 3).map(r => r.id).join(', ') + (resources.length > 3 ? ` +${resources.length - 3} more` : '');

      startPolling(jobId, 'multi_doc_analysis', docLabel);
      setSubmittedJobId(jobId);
      setJobStatus('submitted');
    } catch (err: any) {
      const msg = err?.message || 'Failed to submit analysis. Please try again.';
      setErrorMessage(msg);
      setJobStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setJobStatus('idle');
    setErrorMessage('');
    setElapsedSeconds(0);
    setSubmittedJobId(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle>Multi-Doc Q&A</DialogTitle>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('navigate-page', { detail: { page: 'tasks', taskId: 'multi_doc_analysis' } }));
                onOpenChange(false);
              }}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
            >
              View history →
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 pr-1">
          {/* Section 1 — Select from loaded documents */}
          <div>
            <div className="text-sm font-medium text-gray-800 mb-2">Select from loaded documents</div>
            {patentDocs.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                No patent documents in session. Add documents first or enter numbers below.
              </p>
            ) : (
              <div className="overflow-y-auto max-h-[240px] border border-gray-200 rounded-md">
                <div className="p-2 space-y-1">
                  {patentDocs.map(doc => (
                    <label
                      key={doc.resource_id}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedDocIds.has(doc.resource_id)}
                        onCheckedChange={() => toggleDoc(doc.resource_id)}
                      />
                      <span className="text-sm text-gray-800 truncate">
                        {formatResourceId(doc.resource_id)}
                        {doc.title && doc.title !== formatResourceId(doc.resource_id) && (
                          <span className="text-gray-500 ml-1.5 text-xs truncate">— {doc.title}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Section 2 — Add additional patent documents by number */}
          <div>
            <div className="text-sm font-medium text-gray-800 mb-2">Add by patent number</div>
            <div className="flex gap-2">
              <Textarea
                value={manualInput}
                onChange={e => { setManualInput(e.target.value); setManualInputError(''); }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddManual(); } }}
                placeholder={"e.g. US11941156\nEP3123456\n20230123456"}
                rows={3}
                className="flex-1 resize-none text-sm"
              />
              <Button variant="outline" onClick={handleAddManual} className="gap-1.5 shrink-0 self-start">
                <Plus className="w-4 h-4" />
                Add
              </Button>
            </div>
            {manualInputError && (
              <p className="text-xs text-red-600 mt-1">{manualInputError}</p>
            )}
            {manualDocs.length > 0 && (
              <div className="mt-2 space-y-1">
                {manualDocs.map((doc, i) => (
                  <div key={i} className="flex items-center justify-between px-2 py-1 rounded bg-gray-50 border border-gray-200">
                    <span className="text-sm text-gray-800">{doc.label}</span>
                    <button
                      onClick={() => handleRemoveManual(i)}
                      className="text-gray-400 hover:text-gray-700 ml-2"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 3 — Question */}
          <div>
            <div className="text-sm font-medium text-gray-800 mb-2">Question</div>
            <Textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="Ask a question about these documents…"
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        {/* Section 4 — Status / Submit */}
        <div className="border-t border-gray-200 pt-4 mt-2">
          {jobStatus === 'idle' || jobStatus === 'error' ? (
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={emailResults}
                  onCheckedChange={v => setEmailResults(!!v)}
                />
                <span className="text-sm text-gray-700">Email results to me when complete</span>
              </label>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-600">
                {totalCount > 0 ? `${totalCount} document${totalCount !== 1 ? 's' : ''} selected` : 'No documents selected'}
              </span>
              <div className="flex items-center gap-2">
                {jobStatus === 'error' && (
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    Reset
                  </Button>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Run Analysis
                </Button>
              </div>
            </div>
            </div>
          ) : jobStatus === 'done' ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-green-700 font-medium">Analysis complete.</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleReset}>
                  New Analysis
                </Button>
                <Button size="sm" onClick={() => {
                  window.dispatchEvent(new CustomEvent('navigate-page', { detail: { page: 'tasks', jobId: submittedJobId } }));
                  onOpenChange(false);
                }}>
                  View Results
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600 shrink-0" />
                <span>Processing in background… ({elapsedSeconds}s)</span>
              </div>
              <p className="text-xs text-gray-500">
                You can close this panel. A notification will appear when done.
              </p>
            </div>
          )}

          {jobStatus === 'error' && errorMessage && (
            <p className="text-xs text-red-600 mt-2">{errorMessage}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
